import React, { useEffect, useRef, useState } from "react";

import cx from "classnames";

import type { Criteria } from "features/entries/types/bet-tracker";
import {
  doesValueSatisfyTarget,
  doesValueFailTarget,
} from "features/entries/util/bet-tracker-helpers";
import * as styles from "./ProgressBar.module.scss";

interface ProgressBarProps {
  value: number;
  criteria?: Criteria;
  outcomeResult?: "VOID" | "WIN" | "LOSE" | "UNDECIDED";
  isLive?: boolean;
}

function getTargetInfo(criteria?: Criteria): {
  value: number;
  type: "GT" | "GTE" | "LT" | "LTE" | "EQ";
} {
  if (criteria?.GT !== undefined) return { value: criteria.GT, type: "GT" };
  if (criteria?.GTE !== undefined) return { value: criteria.GTE, type: "GTE" };
  if (criteria?.LT !== undefined) return { value: criteria.LT, type: "LT" };
  if (criteria?.LTE !== undefined) return { value: criteria.LTE, type: "LTE" };
  if (criteria?.EQ !== undefined) return { value: criteria.EQ, type: "EQ" };
  return { value: 1, type: "EQ" };
}

const ProgressBar = ({
  value,
  criteria,
  outcomeResult,
  isLive,
}: ProgressBarProps) => {
  const isVoid = outcomeResult === "VOID";
  const isLost = outcomeResult === "LOSE";
  const isWon = outcomeResult === "WIN";
  const isPrematch = !isLive && outcomeResult === "UNDECIDED";

  const { value: target, type: targetType } = getTargetInfo(criteria);

  const meetsCriteria = doesValueSatisfyTarget(value, criteria);
  const hasBustedTarget = doesValueFailTarget(value, criteria);

  const pct = (() => {
    if (targetType === "EQ" && target === 0) return meetsCriteria ? 100 : 0;
    const safeTarget = target <= 0 ? 1 : target;
    const clampedValue = Math.min(value, safeTarget);
    const raw = target === 0 ? 100 : (clampedValue / safeTarget) * 100;
    return Math.min(Math.max(raw, 0), 100);
  })();

  const noProgress = value === 0 && target > 0;

  const isFirstRender = useRef(true);
  const [isBouncing, setIsBouncing] = useState(false);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setIsBouncing(true);
    const timer = setTimeout(() => setIsBouncing(false), 400);
    return () => clearTimeout(timer);
  }, [pct]);

  return (
    <div className={styles.container}>
      <div className={styles.track}>
        <div
          className={cx(styles.filled, {
            [styles.isWon]: isWon || (meetsCriteria && isLive),
            [styles.noProgress]: noProgress,
            [styles.isLost]: hasBustedTarget && isLive,
            [styles.isSettledLoss]: isLost && !isLive,
            [styles.isVoid]: isVoid,
          })}
          style={{ width: `${pct}%` }}
        />
        <div className={styles.unfilled} style={{ left: `${pct}%` }} />
        <span className={styles.maxLabel}>{target}</span>
        <div
          className={cx(styles.thumb, {
            [styles.isWon]: isWon || (meetsCriteria && isLive),
            [styles.noProgress]: noProgress,
            [styles.isLost]: hasBustedTarget && isLive,
            [styles.isSettledLoss]: isLost && !isLive,
            [styles.isVoid]: isVoid,
            [styles.isPrematch]: isPrematch,
            [styles.isBouncing]: isBouncing,
          })}
          style={{ left: `${pct}%` }}
        >
          {value}
        </div>
      </div>
    </div>
  );
};

export { ProgressBar };
